import EmailIcon from '@mui/icons-material/EmailOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import PercentIcon from '@mui/icons-material/Percent';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { AccordionDetails, AccordionSummary, Button, Chip, Divider, Stack, Typography, useTheme } from '@mui/material';
import { BenefitSettingDTOBenefitEnum } from 'probonio-shared-ui/api';
import {
  SupportAccordion,
  SupportAccordionHeader,
  SupportContactRow,
  SupportSectionTitle,
} from 'probonio-shared-ui/component/support/SupportAccordion';
import { useStaticLinks } from 'probonio-shared-ui/module/links/hooks';
import { useBenefitSettings } from 'probonio-shared-ui/module/me';
import { normalizeTel, normalizeWa } from 'probonio-shared-ui/utils/normalize';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BasicDialog, DialogState } from '../dialog';

/**
 * These benefits are not displayed in the dialog to avoid duplicates.
 */
const EXCLUDED_BENEFITS: BenefitSettingDTOBenefitEnum[] = [
  BenefitSettingDTOBenefitEnum.Bikeleasing,
  BenefitSettingDTOBenefitEnum.Bike,
  BenefitSettingDTOBenefitEnum.Custom,
  BenefitSettingDTOBenefitEnum.CouponsV2,
  BenefitSettingDTOBenefitEnum.Fitness,
];

export const SupportDialogOnePortal: React.FC<{ dialogState: DialogState }> = ({ dialogState }) => {
  const { t, i18n } = useTranslation('homePage');
  const theme = useTheme();
  const links = useStaticLinks();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const benefitSettings = useBenefitSettings();

  return (
    <BasicDialog
      dialogState={dialogState}
      title={<Trans i18nKey="homePage:support.dialogTitle" />}
      content={
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="body1">{t('support.description')}</Typography>
          <Stack
            sx={{
              gap: 1,
              maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <SupportAccordion expanded={expanded === 'general'} onChange={handleChange('general')} sx={{ py: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SupportAccordionHeader
                  icon={<SupportAgentOutlinedIcon />}
                  title={t('support.general.title')}
                  subtitle={t('support.general.subtitle')}
                  color={theme.palette.warning.light}
                  theme={theme}
                />
              </AccordionSummary>

              <AccordionDetails>
                <Stack sx={{ mx: 5, gap: 2 }}>
                  <Stack
                    sx={{
                      gap: 1,
                    }}
                  >
                    {links?.TEL_GENERAL_SUPPORT && (
                      <SupportSectionTitle title={t('support.general.phone')} color={theme.palette.grey['700']}>
                        <SupportContactRow
                          icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.warning.light}
                          href={`tel:${normalizeTel(links.TEL_GENERAL_SUPPORT)}`}
                          text={links.TEL_GENERAL_SUPPORT}
                          theme={theme}
                        />
                      </SupportSectionTitle>
                    )}
                    {links?.WA && (
                      <SupportSectionTitle title={t('support.general.whatsapp')} color={theme.palette.grey['700']}>
                        <SupportContactRow
                          icon={<WhatsAppIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.warning.light}
                          href={`https://wa.me/${normalizeWa(links.WA)}`}
                          text={links.WA}
                          theme={theme}
                        />
                      </SupportSectionTitle>
                    )}
                    {links?.MAIL && (
                      <SupportSectionTitle title={t('support.general.mail')} color={theme.palette.grey['700']}>
                        <SupportContactRow
                          icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.warning.light}
                          href={`mailto:${links.MAIL}`}
                          text={links.MAIL}
                          copyValue={links.MAIL}
                          theme={theme}
                        />
                      </SupportSectionTitle>
                    )}
                  </Stack>

                  <Divider />

                  {links?.MANUAL && (
                    <Button href={links.MANUAL} target="_blank" variant="outlined" sx={{ width: 'fit-content' }} rel="noopener noreferrer">
                      {t('support.helpCenter')}
                    </Button>
                  )}
                </Stack>
              </AccordionDetails>
            </SupportAccordion>

            <SupportAccordion expanded={expanded === 'bike'} disableGutters onChange={handleChange('bike')} sx={{ py: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SupportAccordionHeader
                  icon={<PedalBikeIcon />}
                  title={t('extensions:bikeleasing_v2.support.title')}
                  subtitle={t('extensions:bikeleasing_v2.support.subtitle')}
                  color={theme.palette.success.light}
                  theme={theme}
                />
              </AccordionSummary>

              <AccordionDetails>
                <Stack sx={{ mx: 5, gap: 2 }}>
                  <SupportSectionTitle title={t('extensions:bikeleasing_v2.support.employerInsurance')} color={theme.palette.grey['700']}>
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      {links?.BLS_EMPLOYER_INSURANCE_TEL && (
                        <SupportContactRow
                          icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.success.light}
                          href={`tel:${normalizeTel(links.BLS_EMPLOYER_INSURANCE_TEL)}`}
                          text={links.BLS_EMPLOYER_INSURANCE_TEL}
                          theme={theme}
                        />
                      )}
                      {links?.BLS_EMPLOYER_INSURANCE_MAIL && (
                        <SupportContactRow
                          icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.success.light}
                          href={`mailto:${links.BLS_EMPLOYER_INSURANCE_MAIL}`}
                          text={links.BLS_EMPLOYER_INSURANCE_MAIL}
                          copyValue={links.BLS_EMPLOYER_INSURANCE_MAIL}
                          theme={theme}
                        />
                      )}
                    </Stack>
                  </SupportSectionTitle>
                  <SupportSectionTitle title={t('extensions:bikeleasing_v2.support.bikeInsurance')} color={theme.palette.grey['700']}>
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      {links?.BLS_BIKE_INSURANCE_TEL && (
                        <SupportContactRow
                          icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.success.light}
                          href={`tel:${normalizeTel(links.BLS_BIKE_INSURANCE_TEL)}`}
                          text={links.BLS_BIKE_INSURANCE_TEL}
                          theme={theme}
                        />
                      )}
                      {links?.BLS_BIKE_INSURANCE_MAIL && (
                        <SupportContactRow
                          icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.success.light}
                          href={`mailto:${links.BLS_BIKE_INSURANCE_MAIL}`}
                          text={links.BLS_BIKE_INSURANCE_MAIL}
                          copyValue={links.BLS_BIKE_INSURANCE_MAIL}
                          theme={theme}
                        />
                      )}
                    </Stack>
                  </SupportSectionTitle>
                  <SupportSectionTitle title={t('extensions:bikeleasing_v2.support.otherTopics')} color={theme.palette.grey['700']}>
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      {links?.BLS_OTHER_TOPICS_TEL && (
                        <SupportContactRow
                          icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.success.light}
                          href={`tel:${normalizeTel(links.BLS_OTHER_TOPICS_TEL)}`}
                          text={links.BLS_OTHER_TOPICS_TEL}
                          theme={theme}
                        />
                      )}
                      {links?.BLS_OTHER_TOPICS_MAIL && (
                        <SupportContactRow
                          icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.success.light}
                          href={`mailto:${links.BLS_OTHER_TOPICS_MAIL}`}
                          text={links.BLS_OTHER_TOPICS_MAIL}
                          copyValue={links.BLS_OTHER_TOPICS_MAIL}
                          theme={theme}
                        />
                      )}
                    </Stack>
                  </SupportSectionTitle>

                  {links?.BLS_WA && (
                    <>
                      <Divider />
                      <Button
                        href={`https://wa.me/${normalizeWa(links.BLS_WA)}`}
                        variant="outlined"
                        sx={{ width: 'fit-content' }}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {t('extensions:bikeleasing_v2.support.writeChatbotOnWA')}
                      </Button>
                    </>
                  )}
                </Stack>
              </AccordionDetails>
            </SupportAccordion>

            <SupportAccordion expanded={expanded === 'benefits'} disableGutters onChange={handleChange('benefits')} sx={{ py: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SupportAccordionHeader
                  icon={<PercentIcon />}
                  title={t('support.benefits.title')}
                  subtitle={t('support.benefits.subtitle')}
                  color={theme.palette.info.light}
                  theme={theme}
                />
              </AccordionSummary>

              <AccordionDetails>
                <Stack sx={{ mx: 5, gap: 2 }}>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, my: 1 }}>
                    {benefitSettings.benefits
                      .filter(benefit => !EXCLUDED_BENEFITS.includes(benefit))
                      .map(benefit => t(`common:benefit.${benefit}`))
                      .sort((a, b) => a.localeCompare(b, i18n.language, { sensitivity: 'base' }))
                      .map(item => (
                        <Chip key={item} label={item} sx={{ width: 'fit-content', p: 0 }} />
                      ))}
                  </Stack>
                  <SupportSectionTitle title={t('support.benefits.generalSupport')} color={theme.palette.grey['700']}>
                    <Stack
                      sx={{
                        gap: 1,
                      }}
                    >
                      {links?.TEL_BENEFIT_SUPPORT && (
                        <SupportContactRow
                          icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.info.light}
                          href={`tel:${normalizeTel(links.TEL_BENEFIT_SUPPORT)}`}
                          text={links.TEL_BENEFIT_SUPPORT}
                          theme={theme}
                        />
                      )}
                      {links?.WA && (
                        <SupportContactRow
                          icon={<WhatsAppIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.info.light}
                          href={`https://wa.me/${normalizeWa(links.WA)}`}
                          text={links.WA}
                          theme={theme}
                        />
                      )}
                      {links?.MAIL && (
                        <SupportContactRow
                          icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                          iconBg={theme.palette.info.light}
                          href={`mailto:${links.MAIL}`}
                          text={links.MAIL}
                          copyValue={links.MAIL}
                          theme={theme}
                        />
                      )}
                    </Stack>
                  </SupportSectionTitle>
                  {(links?.TEL_ACCOUNTING || links?.MAIL_ACCOUNTING) && (
                    <SupportSectionTitle title={t('support.benefits.accounting')} color={theme.palette.grey['700']}>
                      <Stack
                        sx={{
                          gap: 1,
                        }}
                      >
                        {links?.TEL_ACCOUNTING && (
                          <SupportContactRow
                            icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                            iconBg={theme.palette.info.light}
                            href={`tel:${normalizeTel(links.TEL_ACCOUNTING)}`}
                            text={links.TEL_ACCOUNTING}
                            theme={theme}
                          />
                        )}
                        {links?.MAIL_ACCOUNTING && (
                          <SupportContactRow
                            icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                            iconBg={theme.palette.info.light}
                            href={`mailto:${links.MAIL_ACCOUNTING}`}
                            text={links.MAIL_ACCOUNTING}
                            copyValue={links.MAIL_ACCOUNTING}
                            theme={theme}
                          />
                        )}
                      </Stack>
                    </SupportSectionTitle>
                  )}
                  {(links?.MAIL_SALES || links?.TEL_SALES) && (
                    <SupportSectionTitle title={t('support.benefits.sales')} color={theme.palette.grey['700']}>
                      <Stack
                        sx={{
                          gap: 1,
                        }}
                      >
                        {links?.TEL_SALES && (
                          <SupportContactRow
                            icon={<SupportAgentOutlinedIcon sx={{ width: 15, height: 15 }} />}
                            iconBg={theme.palette.info.light}
                            href={`tel:${normalizeTel(links.TEL_SALES)}`}
                            text={links.TEL_SALES}
                            theme={theme}
                          />
                        )}
                        {links?.MAIL_SALES && (
                          <SupportContactRow
                            icon={<EmailIcon sx={{ width: 15, height: 15 }} />}
                            iconBg={theme.palette.info.light}
                            href={`mailto:${links.MAIL_SALES}`}
                            text={links.MAIL_SALES}
                            copyValue={links.MAIL_SALES}
                            theme={theme}
                          />
                        )}
                      </Stack>
                    </SupportSectionTitle>
                  )}

                  <Divider />

                  {links?.MANUAL && (
                    <Button href={links.MANUAL} target="_blank" variant="outlined" sx={{ width: 'fit-content' }} rel="noopener noreferrer">
                      {t('support.helpCenter')}
                    </Button>
                  )}
                </Stack>
              </AccordionDetails>
            </SupportAccordion>
          </Stack>
        </Stack>
      }
    />
  );
};
